import { asStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { TeamListItem as TeamListItemType } from "@bezoned/shared/types/api/team/team-list.types.js";
import { AppEventNames } from "@bezoned/shared/types/app-event-names.js";
import { Body1, Spinner } from "@fluentui/react-components";
import { parseAsBoolean, useQueryState } from "nuqs";
import { createRef, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import { useTeamsNavigation } from "../../../hooks/use-teams-navigation.js";
import { trackAuthenticatedOnboardingEvent } from "../../onboarding/onboarding-tracker.js";
import { STARTER_SLOT_CONSUMED_EMPTY_LOBBY_MESSAGE } from "../../subscription/starter-plan-copy.js";
import { TeamCreateButton } from "../team-create/team-create-button.js";
import { TeamCreateDialog } from "../team-create/team-create-dialog.js";
import { useCreateTeamDialog } from "../team-create/use-create-team-dialog.js";
import { TeamEmptyState } from "./team-empty-state.js";
import { TeamListItem } from "./team-list-item.js";
import { shouldShowStarterSlotConsumedMessage } from "./team-lobby-render-model.js";
import { TeamSyntheticListItem } from "./team-synthetic-list-item.js";
import { useTeamCreationLobby } from "./use-team-creation-lobby.js";

interface TeamListProps {
  dispatchFailureToast: (params: { message: string }) => void;
  createTeamDialogOpen: boolean;
  onCreateTeamDialogDismiss: () => void;
  starterSlotConsumed: boolean;
}

type TransitionRef = RefObject<HTMLDivElement | null>;

export const TeamList = ({
  dispatchFailureToast,
  createTeamDialogOpen,
  onCreateTeamDialogDismiss,
  starterSlotConsumed,
}: TeamListProps) => {
  const [reopenCreateTeamDialog, setReopenCreateTeamDialog] = useState(false);
  const hasTrackedLobbyArrival = useRef(false);
  const { navigateToOffice } = useTeamsNavigation();
  const [forceSameWindow] = useQueryState(
    "stage",
    parseAsBoolean.withDefault(false)
  );
  const {
    jobsQuery,
    renderModel,
    teamsQuery,
    dismissFailedJob,
    dismissingJobId,
  } = useTeamCreationLobby(false, {});
  const transitionKeySignature = renderModel.items
    .map((item) => item.key)
    .join("|");
  const transitionRefs = useMemo(() => {
    const refs = new Map<string, TransitionRef>();
    for (const key of transitionKeySignature.split("|")) {
      if (key) {
        refs.set(key, createRef<HTMLDivElement>());
      }
    }
    return refs;
  }, [transitionKeySignature]);

  const getTransitionRef = (key: string): TransitionRef => {
    const transitionRef = transitionRefs.get(key);
    if (!transitionRef) {
      throw new Error(`Missing transition ref for key: ${key}`);
    }
    return transitionRef;
  };

  const { props: createTeamDialogProps } = useCreateTeamDialog({
    onCreateTeamError: (error) => {
      if (error.code === "starter-office-limit-reached") {
        dispatchFailureToast({ message: error.message });
      }
    },
    open: createTeamDialogOpen || reopenCreateTeamDialog,
    setOpen: (open) => {
      if (open) {
        setReopenCreateTeamDialog(true);
      } else {
        setReopenCreateTeamDialog(false);
        if (createTeamDialogOpen) {
          onCreateTeamDialogDismiss();
        }
      }
    },
  });

  const isLoading = teamsQuery.isLoading || jobsQuery.isLoading;
  const showStarterSlotConsumedMessage = shouldShowStarterSlotConsumedMessage({
    hasAccessibleTeams: (teamsQuery.data?.teams.length ?? 0) > 0,
    hasUnresolvedCreationJob: renderModel.shouldPollJobs,
    hasVisibleCreationResult: renderModel.failedItems.length > 0,
    starterSlotConsumed,
  });

  useEffect(() => {
    if (hasTrackedLobbyArrival.current || isLoading || teamsQuery.error) {
      return;
    }

    hasTrackedLobbyArrival.current = true;
    trackAuthenticatedOnboardingEvent(AppEventNames.LobbyArrived);
  }, [isLoading, teamsQuery.error]);

  const handleTeamSelect = async (team: TeamListItemType) => {
    if (isLoading || team.accessRestrictedReason) {
      return;
    }

    try {
      await navigateToOffice({
        forceSameWindow,
        msTeamId: team.id,
      });
    } catch {
      dispatchFailureToast({
        message: "Failed to select office. Please try again.",
      });
    }
  };

  if (isLoading || teamsQuery.error) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <Spinner size="large" />
        <Body1>Loading your teams...</Body1>
      </div>
    );
  }

  const listContent = (() => {
    if (showStarterSlotConsumedMessage) {
      return (
        <section className="m-4 flex flex-col items-center rounded-md border-2 bg-gray-50 p-4 text-center">
          <Body1>{STARTER_SLOT_CONSUMED_EMPTY_LOBBY_MESSAGE}</Body1>
        </section>
      );
    }

    if (renderModel.hasVisibleRows) {
      return (
        <TransitionGroup className="flex flex-col">
          {renderModel.items.map((item, index) => {
            const content = (() => {
              if (item.kind === "synthetic-active") {
                return (
                  <TeamSyntheticListItem
                    kind="active"
                    requestedName={item.job.requestedName}
                    visualStatus={item.visualStatus}
                  />
                );
              }

              if (item.kind === "synthetic-failed") {
                return (
                  <TeamSyntheticListItem
                    dismissDisabled={dismissingJobId === item.job.jobId}
                    kind="failed"
                    onDismiss={asStableCallback(() =>
                      dismissFailedJob(item.job.jobId)
                    )}
                    requestedName={item.job.requestedName}
                  />
                );
              }

              if (item.kind === "team") {
                return (
                  <TeamListItem
                    highlighted={item.highlighted}
                    index={index}
                    onSelect={handleTeamSelect}
                    team={item.team}
                  />
                );
              }

              return null;
            })();

            if (!content) {
              return null;
            }

            if (item.kind === "synthetic-active") {
              const transitionRef = getTransitionRef(item.key);
              return (
                <CSSTransition
                  classNames="fade"
                  key={item.key}
                  nodeRef={transitionRef}
                  timeout={200}
                >
                  <div ref={transitionRef}>{content}</div>
                </CSSTransition>
              );
            }

            if (item.kind === "synthetic-failed") {
              const transitionRef = getTransitionRef(item.key);
              return (
                <CSSTransition
                  classNames="fade"
                  key={item.key}
                  nodeRef={transitionRef}
                  timeout={200}
                >
                  <div ref={transitionRef}>{content}</div>
                </CSSTransition>
              );
            }

            if (item.kind === "team") {
              const transitionRef = getTransitionRef(item.key);
              return (
                <CSSTransition
                  classNames="fade"
                  key={item.key}
                  nodeRef={transitionRef}
                  timeout={200}
                >
                  <div ref={transitionRef}>{content}</div>
                </CSSTransition>
              );
            }

            return null;
          })}
        </TransitionGroup>
      );
    }

    return (
      <>
        <TeamCreateButton onClick={() => setReopenCreateTeamDialog(true)} />
        <TeamEmptyState
          message={
            jobsQuery.isError
              ? "No BeZoned teams found. Please refresh and try again."
              : "No BeZoned teams found. Create your first team to get started!"
          }
        />
      </>
    );
  })();

  return (
    <>
      {listContent}

      <TeamCreateDialog {...createTeamDialogProps} />
    </>
  );
};
